import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  FaBars,
  FaHome,
  FaEnvelope,
  FaBox,
  FaClipboardList,
  FaMoneyBill,
  FaUsers,
  FaSignOutAlt,
} from "react-icons/fa";
import { TbLayoutDashboardFilled } from "react-icons/tb";

const Sidebar = ({ isOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const [adminData, setAdminData] = useState(null);
  const [logoutMessage, setLogoutMessage] = useState(null);
  const [activePage, setActivePage] = useState(null);

  useEffect(() => {
    const storedPage = localStorage.getItem("activePage");
    setActivePage(storedPage || "AdminHomepage");
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("userData");
    const storedRole = localStorage.getItem("userRole");

    if (storedUser && storedRole) {
      try {
        const userData = JSON.parse(storedUser);
        if (storedRole !== "Administrator") {
          navigate("/unauthorized");
        } else {
          setAdminData(userData);
        }
      } catch (error) {
        console.error("Error parsing userData:", error);
        navigate("/unauthorized");
      }
    } else {
      navigate("/unauthorized");
    }
  }, [navigate]);

  // Sync expanded state with isOpen and update localStorage
  useEffect(() => {
    localStorage.setItem("sidebarExpanded", isOpen.toString());
  }, [isOpen]);

  const handleNavigation = (page, route) => {
    if (!isOpen) {
      setIsSidebarOpen(true); // Open sidebar if closed before navigating
      return;
    }
    setActivePage(page);
    localStorage.setItem("activePage", page);
    navigate(route);
  };

  const handleLogout = () => {
    setLogoutMessage("Are you sure you want to logout?");
  };

  const confirmLogout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("userRole");
    localStorage.removeItem("activePage");
    localStorage.removeItem("sidebarExpanded");
    setLogoutMessage("✅ Logged out successfully!");

    setTimeout(() => {
      setLogoutMessage(null);
      navigate("/");
    }, 1500);
  };

  const closeLogoutPopup = () => {
    setLogoutMessage(null);
  };

  if (!adminData) {
    return null;
  }

  return (
    <SidebarContainer $isOpen={isOpen}>
      <ToggleButton onClick={() => setIsSidebarOpen(!isOpen)}>
        <FaBars />
      </ToggleButton>

      <SidebarContent>
        <NavItem
          $active={activePage === "AdminHomepage"}
          $isOpen={isOpen}
          onClick={() => handleNavigation("AdminHomepage", "/AdminHomepage")}
        >
          <FaHome />
          <span>Home</span>
        </NavItem>

        <NavItem
          $active={activePage === "dashboard"}
          $isOpen={isOpen}
          onClick={() => handleNavigation("dashboard", "/dashboard")}
        >
          <TbLayoutDashboardFilled />
          <span>Dashboard</span>
        </NavItem>

        <NavItem
          $active={activePage === "messages"}
          $isOpen={isOpen}
          onClick={() => handleNavigation("messages", "/messages")}
        >
          <FaEnvelope />
          <span>Messages</span>
        </NavItem>

        <NavItem
          $active={activePage === "products"}
          $isOpen={isOpen}
          onClick={() => handleNavigation("products", "/products")}
        >
          <FaBox />
          <span>Products</span>
        </NavItem>

        <NavItem
          $active={activePage === "orders"}
          $isOpen={isOpen}
          onClick={() => handleNavigation("orders", "/orders")}
        >
          <FaClipboardList />
          <span>Orders</span>
        </NavItem>

        <NavItem
          $active={activePage === "sold"}
          $isOpen={isOpen}
          onClick={() => handleNavigation("sold", "/sold")}
        >
          <FaMoneyBill />
          <span>Sold</span>
        </NavItem>

        <NavItem
          $active={activePage === "userlist"}
          $isOpen={isOpen}
          onClick={() => handleNavigation("userlist", "/userlist")}
        >
          <FaUsers />
          <span>User List</span>
        </NavItem>

        <LogoutNavItem
          $active={false}
          $isOpen={isOpen}
          onClick={handleLogout}
        >
          <FaSignOutAlt />
          <span>Log Out</span>
        </LogoutNavItem>
      </SidebarContent>

      {logoutMessage && (
        <LogoutPopup>
          <PopupMessage>{logoutMessage}</PopupMessage>
          {logoutMessage === "Are you sure you want to logout?" && (
            <PopupButtons>
              <PopupButton onClick={confirmLogout}>Yes</PopupButton>
              <PopupButton onClick={closeLogoutPopup}>No</PopupButton>
            </PopupButtons>
          )}
        </LogoutPopup>
      )}
    </SidebarContainer>
  );
};

export default Sidebar;

/* === Styled Components === */

const SidebarContainer = styled.div`
  position: fixed;
  left: 0;
  top: 112.5px;
  width: ${({ $isOpen }) => ($isOpen ? "200px" : "50px")};
  height: 100vh;
  background-color: #21551c;
  color: white;
  transition: width 0.5s ease;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  z-index: 1000;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 22px;
  cursor: pointer;
  padding: 15px;
  text-align: left;
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 5px;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  font-size: 1.2rem;
  font-weight: bold;
  color: white;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  transition: all 0.3s ease-in-out;

  ${({ $active }) =>
    $active &&
    `
    background: white;
    color: #046600;
    border-radius: 1.2px;
  `}

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  svg {
    min-width: 30px;
    font-size: 22px;
    cursor: pointer;
  }

  span {
    opacity: ${({ $isOpen }) => ($isOpen ? "1" : "0")};
    display: ${({ $isOpen }) => ($isOpen ? "inline" : "none")};
    transition: opacity 0.3s ease-in-out;
  }
`;

const LogoutNavItem = styled(NavItem)`
  ${({ $active }) =>
    $active &&
    `
    background: white;
    color: #046600;
    border-radius: 1.2px;
  `}

  &:hover {
    background: #c9302c;
  }
`;

const LogoutPopup = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
  text-align: center;
  z-index: 1000;
  width: 300px;
`;

const PopupMessage = styled.p`
  font-size: 1.2rem;
  color: black;
`;

const PopupButtons = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 10px;
`;

const PopupButton = styled.button`
  padding: 10px 20px;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  border-radius: 5px;
  background: #046600;
  color: white;

  &:hover {
    background: red;
  }
`;